// Tiny safe expression evaluator for user-authored calculation-table formulas.
// No eval / new Function. Shared verbatim with the server
// (crmServer/src/services/calcFormula.ts) — keep the two copies in sync.
//
// Grammar (low → high precedence):
//   ternary     : or ( '?' expr ':' expr )?
//   comparison  : add ( ('=='|'!='|'<'|'>'|'<='|'>=') add )?
//   additive    : mul ( ('+'|'-') mul )*
//   multiplic.  : unary ( ('*'|'/') unary )*
//   unary       : ('-'|'+')? primary
//   primary     : number | ident | ident '(' args ')' | '(' expr ')'
//
// Identifiers resolve from the eval context: other column keys plus the rate
// constants `vat`, `ep`, `mil` (fractions, e.g. 0.20). Unknown / blank → 0.
// Division by zero → 0. A NaN/Infinity result is coerced to 0.

const FUNCTIONS: { [name: string]: { min: number, max: number, apply: (a: number[]) => number } } = {
    round: { min: 1, max: 2, apply: (a) => { const f = Math.pow(10, a[1] ?? 0); return Math.round((a[0] + Number.EPSILON) * f) / f; } },
    floor: { min: 1, max: 1, apply: (a) => Math.floor(a[0]) },
    ceil: { min: 1, max: 1, apply: (a) => Math.ceil(a[0]) },
    abs: { min: 1, max: 1, apply: (a) => Math.abs(a[0]) },
    min: { min: 1, max: 64, apply: (a) => Math.min(...a) },
    max: { min: 1, max: 64, apply: (a) => Math.max(...a) },
    if: { min: 3, max: 3, apply: (a) => (a[0] !== 0 ? a[1] : a[2]) },
};

const RESERVED = new Set(['vat', 'ep', 'mil']);

type Tok = { t: 'num', v: number } | { t: 'id', v: string } | { t: 'op', v: string };

function tokenize(src: string): Tok[] {
    const out: Tok[] = [];
    let i = 0;
    const two = ['==', '!=', '<=', '>='];
    while (i < src.length) {
        const ch = src[i];
        if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { i++; continue; }
        if ((ch >= '0' && ch <= '9') || (ch === '.' && src[i + 1] >= '0' && src[i + 1] <= '9')) {
            let j = i + 1;
            while (j < src.length && ((src[j] >= '0' && src[j] <= '9') || src[j] === '.')) j++;
            const num = Number(src.slice(i, j));
            if (!Number.isFinite(num)) throw new Error(`Некорректное число: ${src.slice(i, j)}`);
            out.push({ t: 'num', v: num });
            i = j;
            continue;
        }
        if ((ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_') {
            let j = i + 1;
            while (j < src.length && (
                (src[j] >= 'a' && src[j] <= 'z') || (src[j] >= 'A' && src[j] <= 'Z') ||
                (src[j] >= '0' && src[j] <= '9') || src[j] === '_')) j++;
            out.push({ t: 'id', v: src.slice(i, j) });
            i = j;
            continue;
        }
        if (i + 1 < src.length && two.includes(src.slice(i, i + 2))) {
            out.push({ t: 'op', v: src.slice(i, i + 2) });
            i += 2;
            continue;
        }
        if ('+-*/()?:,<>'.includes(ch)) {
            out.push({ t: 'op', v: ch });
            i++;
            continue;
        }
        throw new Error(`Неожиданный символ: "${ch}"`);
    }
    return out;
}

type Node =
    | { k: 'num', v: number }
    | { k: 'ref', name: string }
    | { k: 'un', op: string, x: Node }
    | { k: 'bin', op: string, a: Node, b: Node }
    | { k: 'cmp', op: string, a: Node, b: Node }
    | { k: 'tern', c: Node, a: Node, b: Node }
    | { k: 'call', name: string, args: Node[] };

class Parser {
    private p = 0;
    constructor(private toks: Tok[]) {}

    parse(): Node {
        const n = this.ternary();
        if (this.p !== this.toks.length) throw new Error('Лишние символы в конце выражения');
        return n;
    }
    private peek(): Tok | undefined { return this.toks[this.p]; }
    private eat(v?: string): Tok {
        const t = this.toks[this.p];
        if (!t) throw new Error('Неожиданный конец выражения');
        if (v !== undefined && !(t.t === 'op' && t.v === v)) throw new Error(`Ожидалось "${v}"`);
        this.p++;
        return t;
    }
    private isOp(v: string): boolean {
        const t = this.peek();
        return !!t && t.t === 'op' && t.v === v;
    }

    private ternary(): Node {
        const c = this.comparison();
        if (this.isOp('?')) {
            this.eat('?');
            const a = this.ternary();
            this.eat(':');
            const b = this.ternary();
            return { k: 'tern', c, a, b };
        }
        return c;
    }
    private comparison(): Node {
        let a = this.additive();
        const t = this.peek();
        if (t && t.t === 'op' && ['==', '!=', '<', '>', '<=', '>='].includes(t.v)) {
            this.p++;
            const b = this.additive();
            a = { k: 'cmp', op: t.v, a, b };
        }
        return a;
    }
    private additive(): Node {
        let a = this.multiplicative();
        while (this.isOp('+') || this.isOp('-')) {
            const op = this.eat().v as string;
            a = { k: 'bin', op, a, b: this.multiplicative() };
        }
        return a;
    }
    private multiplicative(): Node {
        let a = this.unary();
        while (this.isOp('*') || this.isOp('/')) {
            const op = this.eat().v as string;
            a = { k: 'bin', op, a, b: this.unary() };
        }
        return a;
    }
    private unary(): Node {
        if (this.isOp('-') || this.isOp('+')) {
            const op = this.eat().v as string;
            return { k: 'un', op, x: this.unary() };
        }
        return this.primary();
    }
    private primary(): Node {
        const t = this.peek();
        if (!t) throw new Error('Неожиданный конец выражения');
        if (t.t === 'num') { this.p++; return { k: 'num', v: t.v }; }
        if (t.t === 'op' && t.v === '(') {
            this.eat('(');
            const n = this.ternary();
            this.eat(')');
            return n;
        }
        if (t.t === 'id') {
            this.p++;
            if (this.isOp('(')) {
                this.eat('(');
                const args: Node[] = [];
                if (!this.isOp(')')) {
                    args.push(this.ternary());
                    while (this.isOp(',')) { this.eat(','); args.push(this.ternary()); }
                }
                this.eat(')');
                const fn = FUNCTIONS[t.v];
                if (!fn) throw new Error(`Неизвестная функция: ${t.v}()`);
                if (args.length < fn.min || args.length > fn.max) {
                    throw new Error(`${t.v}() ожидает ${fn.min === fn.max ? fn.min : `${fn.min}–${fn.max}`} аргумент(ов)`);
                }
                return { k: 'call', name: t.v, args };
            }
            return { k: 'ref', name: t.v };
        }
        throw new Error(`Неожиданный токен: "${t.v}"`);
    }
}

const num = (x: number): number => (Number.isFinite(x) ? x : 0);

function evalNode(n: Node, ctx: { [k: string]: number }): number {
    switch (n.k) {
        case 'num': return n.v;
        case 'ref': return num(ctx[n.name] ?? 0);
        case 'un': return n.op === '-' ? -evalNode(n.x, ctx) : evalNode(n.x, ctx);
        case 'call': return num(FUNCTIONS[n.name].apply(n.args.map((a) => evalNode(a, ctx))));
        case 'cmp': {
            const a = evalNode(n.a, ctx), b = evalNode(n.b, ctx);
            switch (n.op) {
                case '==': return a === b ? 1 : 0;
                case '!=': return a !== b ? 1 : 0;
                case '<': return a < b ? 1 : 0;
                case '>': return a > b ? 1 : 0;
                case '<=': return a <= b ? 1 : 0;
                default: return a >= b ? 1 : 0;
            }
        }
        case 'tern': return evalNode(n.c, ctx) !== 0 ? evalNode(n.a, ctx) : evalNode(n.b, ctx);
        case 'bin': {
            const a = evalNode(n.a, ctx), b = evalNode(n.b, ctx);
            switch (n.op) {
                case '+': return num(a + b);
                case '-': return num(a - b);
                case '*': return num(a * b);
                default: return b === 0 ? 0 : num(a / b);
            }
        }
    }
}

function collectRefs(n: Node, acc: Set<string>): void {
    switch (n.k) {
        case 'ref': acc.add(n.name); break;
        case 'un': collectRefs(n.x, acc); break;
        case 'bin': case 'cmp': collectRefs(n.a, acc); collectRefs(n.b, acc); break;
        case 'tern': collectRefs(n.c, acc); collectRefs(n.a, acc); collectRefs(n.b, acc); break;
        case 'call': n.args.forEach((a) => collectRefs(a, acc)); break;
    }
}

export type CompiledFormula = {
    /** identifiers referenced, excluding function names and the rate constants */
    refs: string[];
    eval: (ctx: { [k: string]: number }) => number;
};

export function compileFormula(src: string):
    { ok: true, compiled: CompiledFormula } | { ok: false, error: string } {
    try {
        const trimmed = `${src ?? ''}`.trim();
        if (!trimmed) return { ok: false, error: 'Пустая формула' };
        const node = new Parser(tokenize(trimmed)).parse();
        const all = new Set<string>();
        collectRefs(node, all);
        const refs = Array.from(all).filter((r) => !RESERVED.has(r));
        return { ok: true, compiled: { refs, eval: (ctx) => num(evalNode(node, ctx)) } };
    } catch (e: any) {
        return { ok: false, error: e?.message || 'Ошибка в формуле' };
    }
}

// Order formula columns so every one is evaluated after its dependencies.
// `available` = keys that need no ordering (input columns, constants).
export function orderFormulaColumns(
    cols: Array<{ key: string, refs: string[] }>,
    available: Set<string>,
): { order: string[], cyclic: string[] } {
    const keys = new Set(cols.map((c) => c.key));
    const deps: { [k: string]: string[] } = {};
    const indeg: { [k: string]: number } = {};
    for (const c of cols) {
        deps[c.key] = c.refs.filter((r) => keys.has(r) && r !== c.key);
        indeg[c.key] = deps[c.key].length;
    }
    const queue = cols.filter((c) => indeg[c.key] === 0).map((c) => c.key);
    const order: string[] = [];
    while (queue.length) {
        const k = queue.shift() as string;
        order.push(k);
        for (const c of cols) {
            if (deps[c.key].includes(k)) {
                indeg[c.key]--;
                if (indeg[c.key] === 0) queue.push(c.key);
            }
        }
    }
    const cyclic = cols.filter((c) => !order.includes(c.key)).map((c) => c.key);
    // keep `available` referenced only for symmetry with the server signature
    void available;
    return { order, cyclic };
}
